import React, { useState } from "react";
import { useCommonResponses, CR_MIME } from "@/context/CommonResponsesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Plus,
    X,
    Sparkles,
    GripVertical,
    Trash2,
    Save,
    Loader2,
    Pencil,
    Copy,
} from "lucide-react";

/**
 * Floating right-side drawer with the consultant's saved snippets.
 * Toggle button lives in AppShell (`common-responses-toggle`).
 */
export default function CommonResponsesSidebar() {
    const { isConsultant, items, loaded, open, setOpen, create, update, remove } =
        useCommonResponses();
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null); // {id, title, body, category} | null
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [category, setCategory] = useState("");
    const [saving, setSaving] = useState(false);

    // Group by category (preserving encounter order per group)
    const groups = React.useMemo(() => {
        const map = new Map();
        for (const it of items) {
            const key = (it.category || "").trim() || "Uncategorised";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(it);
        }
        return Array.from(map.entries()).sort(([a], [b]) => {
            if (a === "Uncategorised") return 1;
            if (b === "Uncategorised") return -1;
            return a.localeCompare(b);
        });
    }, [items]);

    const knownCategories = React.useMemo(() => {
        const set = new Set();
        items.forEach((i) => {
            const c = (i.category || "").trim();
            if (c) set.add(c);
        });
        return Array.from(set).sort();
    }, [items]);

    if (!isConsultant) return null;

    const openNew = () => {
        setEditing(null);
        setTitle("");
        setBody("");
        setCategory("");
        setShowForm(true);
    };
    const openEdit = (it) => {
        setEditing(it);
        setTitle(it.title);
        setBody(it.body);
        setCategory(it.category || "");
        setShowForm(true);
    };
    const cancel = () => {
        setShowForm(false);
        setEditing(null);
    };
    const submit = async (e) => {
        e?.preventDefault?.();
        const t = title.trim();
        const b = body.trim();
        const c = category.trim();
        if (!t || !b) return;
        setSaving(true);
        try {
            if (editing) {
                await update(editing.id, { title: t, body: b, category: c });
            } else {
                await create({ title: t, body: b, category: c });
            }
            setShowForm(false);
            setEditing(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {open && (
                <aside
                    className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-card border-l border-border z-40 flex flex-col shadow-2xl"
                    data-testid="common-responses-sidebar"
                >
                    <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-foreground text-background grid place-items-center">
                                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="font-display font-semibold">Common responses</div>
                                <div className="mono-label">
                                    {items.length} saved · drag any into a note or task
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="rounded-full p-2 hover:bg-muted"
                            aria-label="Close"
                            data-testid="cr-close"
                        >
                            <X className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </header>

                    <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="cr-list">
                        {!loaded && (
                            <div className="text-sm text-muted-foreground grid place-items-center py-10">
                                <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
                            </div>
                        )}
                        {loaded && items.length === 0 && !showForm && (
                            <div className="text-center py-10">
                                <div className="w-12 h-12 rounded-full bg-muted mx-auto grid place-items-center">
                                    <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                                </div>
                                <h4 className="font-display font-semibold text-lg mt-4">
                                    Save Your First Response
                                </h4>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Snippets you can drag into any compliance note, task or evidence field.
                                </p>
                            </div>
                        )}
                        {loaded && items.length > 0 && !showForm && (
                            <div data-testid="cr-groups" className="space-y-4">
                                {groups.map(([category, rows]) => (
                                    <div key={category}>
                                        <div className="mono-label mb-2 flex items-center gap-2">
                                            <span>{category}</span>
                                            <span className="rounded-full border border-border px-1.5 text-xs">
                                                {rows.length}
                                            </span>
                                        </div>
                                        <div className="space-y-2">
                                            {rows.map((it) => (
                                                <SnippetCard
                                                    key={it.id}
                                                    item={it}
                                                    onEdit={() => openEdit(it)}
                                                    onDelete={() => {
                                                        if (window.confirm(`Delete "${it.title}"?`)) remove(it.id);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {showForm && (
                            <form
                                onSubmit={submit}
                                className="rounded-2xl border border-foreground/40 bg-background/40 p-4 space-y-3"
                                data-testid="cr-form"
                            >
                                <div>
                                    <Label htmlFor="cr-title" className="mono-label">
                                        Title
                                    </Label>
                                    <Input
                                        id="cr-title"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="e.g. MFA standard response"
                                        className="mt-1"
                                        data-testid="cr-title-input"
                                        maxLength={120}
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="cr-body" className="mono-label">
                                        Response text
                                    </Label>
                                    <Textarea
                                        id="cr-body"
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder="Paste the exact wording you want auto-filled into notes / tasks."
                                        className="mt-1 min-h-[120px] resize-y"
                                        data-testid="cr-body-input"
                                        maxLength={6000}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="cr-category" className="mono-label">
                                        Category (optional)
                                    </Label>
                                    <Input
                                        id="cr-category"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        list="cr-category-list"
                                        placeholder="e.g. MFA, Access, Logging"
                                        className="mt-1"
                                        data-testid="cr-category-input"
                                        maxLength={40}
                                    />
                                    <datalist id="cr-category-list">
                                        {knownCategories.map((c) => (
                                            <option key={c} value={c} />
                                        ))}
                                    </datalist>
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={cancel}
                                        className="rounded-full"
                                        data-testid="cr-cancel"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={saving || !title.trim() || !body.trim()}
                                        className="rounded-md font-display"
                                        data-testid="cr-save"
                                    >
                                        {saving ? (
                                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} />
                                        ) : (
                                            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                                        )}
                                        {editing ? "Update Snippet" : "Save Snippet"}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>

                    {!showForm && (
                        <div className="p-4 border-t border-border">
                            <Button
                                onClick={openNew}
                                className="w-full rounded-md font-display h-12"
                                data-testid="cr-new-btn"
                            >
                                <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                                Save A New Response
                            </Button>
                        </div>
                    )}
                </aside>
            )}
        </>
    );
}

function SnippetCard({ item, onEdit, onDelete }) {
    const onDragStart = (e) => {
        e.dataTransfer.setData(CR_MIME, item.body);
        e.dataTransfer.setData("text/plain", item.body);
        e.dataTransfer.effectAllowed = "copy";
    };
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(item.body);
        } catch {
            /* no-op */
        }
    };
    return (
        <div
            className="rounded-2xl border border-border bg-background/40 p-3 hover:border-foreground/40 transition-colors"
            data-testid={`cr-item-${item.id}`}
        >
            <div
                className="flex items-start gap-2 cursor-grab active:cursor-grabbing"
                draggable
                onDragStart={onDragStart}
                data-testid={`cr-drag-${item.id}`}
            >
                <GripVertical className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate" title={item.title}>
                        {item.title}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-3 whitespace-pre-wrap">
                        {item.body}
                    </p>
                </div>
            </div>
            <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-border/60">
                <button
                    onClick={copyToClipboard}
                    className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                    aria-label="Copy to clipboard"
                    data-testid={`cr-copy-${item.id}`}
                >
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button
                    onClick={onEdit}
                    className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                    aria-label="Edit"
                    data-testid={`cr-edit-${item.id}`}
                >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button
                    onClick={onDelete}
                    className="rounded-full p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    aria-label="Delete"
                    data-testid={`cr-delete-${item.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
}
